<template>
  <div class="signup-modal__shell" :class="{ 'signup-modal__shell--plain': hideBranding }" data-testid="signup-modal-wrapper">
    <section class="signup-modal__form" aria-label="Sign up for Zight">
      <!-- Defensive: hide Zight branding on white-label orgs in case this modal is reached from any non-CTA path. -->
      <div v-if="!hideBranding" class="signup-modal__branding" data-testid="sign-up-modal-branding">
        <ImageSvg name="general.logoWithText" />
      </div>

      <TitleSeparator class="signup-modal__divider">Sign up with</TitleSeparator>
      <SocialAuth :redirect-url="props.redirectUrl" @action="onSocialAction" :in-progress="socialInProgress" />
      <TitleSeparator class="signup-modal__divider signup-modal__divider--compact">Or</TitleSeparator>

      <Form @submit="onSubmit" :validation-schema="schema">
        <fieldset class="signup-modal__fields">
          <div class="signup-modal__field">
            <label htmlFor="emailInput" class="signup-modal__label">Work email</label>
            <Field type="text" name="email" id="emailInput" data-testid="sign-up-email" class="signup-modal__input" />
            <ErrorMessage as="p" class="signup-modal__error" name="email" />
          </div>
          <div class="signup-modal__field">
            <label htmlFor="passwordInput" class="signup-modal__label">Password</label>
            <Field type="password" name="password" id="passwordInput" data-testid="sign-up-password" class="signup-modal__input" />
            <ErrorMessage as="p" class="signup-modal__error" name="password" />
            <p class="signup-modal__password-hint">Must be at least 8 characters long, contain uppercase and lowercase letters and a number.</p>
          </div>
          <Button
            class="!w-full signup-modal__submit"
            data-testid="sign-up"
            :disabled="requestInProgress"
            :loading="requestInProgress"
            type="solid"
            variant="blue"
            :submit="true"
          >
            Sign up
          </Button>
        </fieldset>
      </Form>

      <p class="signup-modal__signin">
        Have an account?
        <a @mousedown.prevent @click.stop.prevent="showSignInModal(props.redirectUrl)" data-testid="sign-in-show-modal" href="#">Sign in</a>
      </p>

      <div v-if="!hideBranding" class="signup-modal__social-proof" data-testid="signup-social-proof">
        <p class="signup-modal__social-proof-title">Trusted by thousands of teams</p>
        <ul class="signup-modal__social-proof-logos" role="list">
          <li><img :src="logoAdobe" width="61" height="16" alt="Adobe" /></li>
          <li><img :src="logoNotion" width="44" height="16" alt="Notion" /></li>
          <li><img :src="logoGainsight" width="73" height="16" alt="Gainsight" /></li>
          <li><img :src="logoBasecamp" width="73" height="16" alt="Basecamp" /></li>
        </ul>
      </div>
    </section>

    <aside v-if="!hideBranding" class="signup-modal__promo" data-testid="signup-promo-box" aria-label="Zight Agents benefits">
      <h2 class="signup-modal__promo-title">Record it once.<br />Let the agent do it forever.</h2>
      <p class="signup-modal__promo-subtitle">With Zight Agents, you can:</p>
      <ul role="list" class="signup-modal__promo-list">
        <li><Icon name="general.checkedCircleSolid" /><span>Turn any recording into a reusable AI agent</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Automate repetitive workflows without code</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Share one link—no more repeat explanations</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Onboard teammates faster with step-by-step agents</span></li>
        <li><Icon name="general.checkedCircleSolid" /><span>Works instantly in your browser—no setup required</span></li>
      </ul>
      <a
        href="https://zight.com/agent/?utm_source=product&utm_medium=web&utm_campaign=signup_login_agent_promo&utm_content=see_how_it_works"
        target="_blank"
        rel="noopener noreferrer"
        class="signup-modal__promo-cta"
      >
        See how it works
      </a>
      <button v-if="props.showModalForFreeTier" data-testid="signup-skip-button" @click="onSkip" class="signup-modal__skip-button">
        Skip sign up for now &rarr;
      </button>
      <img :src="agentsPreview" alt="" aria-hidden="true" class="signup-modal__promo-image" />
    </aside>
  </div>
</template>
<script setup lang="ts">
import { object, string } from 'yup';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { useUserStore } from '@/store/useUserStore';
import SocialAuth from './auth_dialog/Social.vue';
import TitleSeparator from './auth_dialog/TitleSeparator.vue';
import { useToast } from '@/composables/useToast';
import { useAuthModals } from '@/composables/useAuthModals';
import { useBranding } from '@/composables/useBranding';
import { ref } from 'vue';
import { Button, ImageSvg, Icon } from '@/storybook';
import agentsPreview from './sign-up-agents-preview.png';
import logoAdobe from './social-proof-adobe.svg';
import logoNotion from './social-proof-notion.svg';
import logoGainsight from './social-proof-gainsight.svg';
import logoBasecamp from './social-proof-basecamp.svg';
import { useAuth } from '@/composables/useAuth';

const hasUpperCase = /[A-Z]/;
const hasNumber = /[0-9]/;
const hasLowerCase = /[a-z]/;

const requestInProgress = ref(false);
const userStore = useUserStore();
const { showSignInModal } = useAuthModals();
const { hideBranding } = useBranding();

const props = withDefaults(
  defineProps<{
    redirectUrl?: string;
    sidebarText?: string;
    showModalForFreeTier?: boolean;
    signedUpFrom?: string;
  }>(),
  {
    sidebarText: 'Create videos, GIFs, annotated screenshots and share instantly',
    showModalForFreeTier: false,
    signedUpFrom: undefined,
  }
);

const schema = object().shape({
  email: string().email().required(),
  password: string()
    .required()
    .min(8, 'Must be at least 8 characters long, contain uppercase and lowercase letters and a number')
    .test('isValidPass', 'Must be at least 8 characters long, contain uppercase and lowercase letters and a number', (value: string | undefined) => {
      return [hasUpperCase, hasLowerCase, hasNumber].every((val: RegExp) => val.test(value || ''));
    }),
});

const { onSocialAction, inProgress: socialInProgress } = useAuth(props.redirectUrl);

const { error } = useToast();
const emits = defineEmits(['close']);

const onSubmit = async (data: any) => {
  try {
    requestInProgress.value = true;
    const response = await userStore.signUp(data, props.signedUpFrom);

    if (response && props.redirectUrl) {
      window.location.assign(props.redirectUrl);
      return;
    }

    if (response && response.metadata && response.metadata.redirectTo) {
      window.location.assign(response.metadata.redirectTo);
      return;
    }

    await userStore.load();

    requestInProgress.value = false;
    emits('close');
  } catch (err: any) {
    requestInProgress.value = false;
    error(err.message);
  }
};

const onSkip = () => emits('close');
</script>

<style scoped>
.signup-modal__shell {
  width: min(100vw - 2rem, 55rem);
  display: grid;
  grid-template-columns: minmax(20rem, 1fr) minmax(18rem, 0.9fr);
  gap: 0;
  overflow: hidden;
  border-radius: 1.5rem;
  background: var(--white-copernicus, #fff);
  box-shadow:
    0 1.25rem 3.75rem rgba(15, 23, 42, 0.22),
    0 0 0 1px rgba(148, 163, 184, 0.18);
}

.signup-modal__shell--plain {
  width: min(100vw - 2rem, 28rem);
  grid-template-columns: 1fr;
}

.signup-modal__form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16.5px;
  padding: 3rem 3.75rem;
  background: var(--white-copernicus, #fff);
  color: var(--zinc-900, #18181b);
}

.signup-modal__branding {
  display: flex;
  justify-content: center;
}

.signup-modal__branding :deep(svg),
.signup-modal__branding :deep(img) {
  max-height: 2.75rem;
}

.signup-modal__divider {
  --divider-background: var(--white-copernicus, #fff);
  --divider-stroke: #d8e0ec;
  --divider-color: #64748b;
  margin-block: 0 !important;
}

.signup-modal__divider--compact {
  margin-block: 0.25rem !important;
}

.signup-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 16.5px;
  margin: 0;
  padding: 0;
  border: none;
}

.signup-modal__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.signup-modal__label {
  color: #334155;
  font-size: 0.875rem;
  font-weight: 700;
}

.signup-modal__input {
  width: 100%;
  height: 34px;
  padding: 0 0.75rem;
  border: 1px solid #cdd5e1;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  background: #ffffff;
  color: #0f172a;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.signup-modal__input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.25rem rgba(37, 99, 235, 0.18);
}

.signup-modal__error {
  margin-block-start: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.signup-modal__submit {
  min-height: 3.25rem;
  border-radius: 100px;
  font-size: 14px !important;
  font-weight: 700 !important;
}

.signup-modal__password-hint {
  margin-block: 0.25rem 0;
  color: #64748b;
  font-size: 0.8125rem;
}

.signup-modal__signin {
  margin-block: 0;
  color: #475569;
  font-size: 0.875rem;
  text-align: center;
}

.signup-modal__social-proof {
  text-align: center;
}

.signup-modal__social-proof-title {
  color: #94a3b8;
  font-size: 0.875rem;
  margin-block: 0 0.75rem;
}

.signup-modal__social-proof-logos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  padding: 0;
  list-style: none;
  margin: 0;
}

.signup-modal__signin a,
.signup-modal__promo-link a {
  color: #2563eb;
  text-decoration: none;
  border-radius: 0.25rem;
}

.signup-modal__signin a:hover,
.signup-modal__promo-link a:hover {
  color: #60a5fa;
}

.signup-modal__signin a:focus-visible,
.signup-modal__promo-link a:focus-visible,
.signup-modal__skip-button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 0.25rem rgba(37, 99, 235, 0.22);
}

.signup-modal__promo {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 36rem;
  padding: 3.5rem 2.5rem 0;
  color: var(--white-copernicus, #fff);
  background: radial-gradient(ellipse at 80% 10%, rgba(255, 255, 255, 0.12) 0, transparent 50%), #77448d;
  overflow: hidden;
}

.signup-modal__promo > * {
  margin-block: 0;
}

.signup-modal__promo-title {
  color: var(--white-copernicus, #fff);
  font-size: clamp(1.25rem, 2.25vw, 1.625rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.signup-modal__promo-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8125rem;
  font-weight: 500;
}

.signup-modal__promo-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  list-style: none;
}

.signup-modal__promo-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
}

.signup-modal__promo-cta {
  display: inline-block;
  align-self: flex-start;
  padding: 0.5625rem 1.25rem;
  border: 1.5px solid rgba(255, 255, 255, 0.75);
  border-radius: 100px;
  background: transparent;
  color: var(--white-copernicus, #fff);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.signup-modal__promo-cta:hover {
  background: var(--white-copernicus, #fff);
  color: #43316f;
}

.signup-modal__promo-image {
  margin-block-start: auto;
  width: 100%;
  border-radius: 0.75rem 0.75rem 0 0;
  display: block;
}

.signup-modal__skip-button {
  align-self: flex-start;
  padding: 0.625rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 0.75rem;
  background: transparent;
  color: var(--white-copernicus, #fff);
  font-weight: 700;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.signup-modal__skip-button:hover {
  background: var(--white-copernicus, #fff);
  color: #43316f;
}

@media (max-width: 760px) {
  .signup-modal__shell {
    width: min(100vw - 2.5rem, 28rem);
    margin-block-start: 0.75rem;
    grid-template-columns: 1fr;
    max-height: calc(100vh - 3rem);
    overflow-y: auto;
    border-radius: 1.25rem;
  }

  .signup-modal__form {
    padding: 2rem 1.5rem 1.5rem;
  }

  .signup-modal__promo {
    min-height: auto;
    padding: 1.5rem 1.5rem 0;
    margin: 0 0.75rem 0.75rem;
    border-radius: 1rem;
  }

  .signup-modal__promo-title {
    font-size: 1.75rem;
  }

  .signup-modal__promo-image {
    width: 100%;
  }
}
</style>
